import {
    extractCommandsFromToolResult,
    InstructionCommandData,
    Interceptor,
    isToolCompleteCommand,
    PromptResponse,
    ResponseCommand
} from '@panaya/ai-chat/domain';
import { RequestResourcesInstruction } from '@panaya/ai-chat/shared';
import { defer, of } from 'rxjs';
import { inject } from '@angular/core';
import { PromptBarService } from './prompt-bar.service';
import { AttachmentChipsService } from '../../../attachments/attachment-chips.service';
import { ResourceRequestOptions } from '../../../attachments/model/resource-request-options.model';

/**
 * Reads the turn's instructions for the prompt bar. `UPLOAD_FILE` shows the paperclip, as before. `REQUEST_RESOURCES`
 * hands the agent's options from the last such instruction to the chips service, which drives the "+" menu. A turn
 * without either clears both. Instructions are read at the top level and inside each `tool_complete` result.
 */
export function promptBarFileInstructionInterceptor(): Interceptor<PromptResponse> {
    const service = inject(PromptBarService);
    const chips = inject(AttachmentChipsService);

    return (rawResponse: PromptResponse) =>
        defer(() => {
            const instructions = withToolResultCommands(rawResponse.commands).flatMap((command) =>
                command.type === 'instruction' ? [command.data] : []
            );

            const resourceRequests = instructions.filter(isRequestResourcesInstruction);

            service.setNeedToUploadFile(instructions.some(isUploadFileInstruction));

            chips.setResourceRequestOptions(
                resourceRequests.length > 0 ? toResourceRequestOptions(resourceRequests[resourceRequests.length - 1]) : undefined
            );

            return of(rawResponse);
        });
}

/** The commands at the top level, with each `tool_complete` replaced by the commands its result carries. */
function withToolResultCommands(commands: ResponseCommand[]): ResponseCommand[] {
    return commands.flatMap((command) =>
        isToolCompleteCommand(command) ? extractCommandsFromToolResult(command.tool_result) ?? [] : [command]
    );
}

function toResourceRequestOptions(instruction: RequestResourcesInstruction): ResourceRequestOptions {
    const options: Partial<RequestResourcesInstruction> = { ...instruction };

    delete options.type;

    return options;
}

function isUploadFileInstruction(data: InstructionCommandData): boolean {
    return !!data && data['type'] === 'UPLOAD_FILE';
}

function isRequestResourcesInstruction(data: InstructionCommandData): data is InstructionCommandData & RequestResourcesInstruction {
    return !!data && data['type'] === 'REQUEST_RESOURCES';
}
